<script setup>

import AccessMethodCard from "./AccessMethodCard.vue";
import Button from "@/components/Button.vue";
import Card from "@/components/Card.vue";
import {computed, onMounted, ref, watch} from "vue";
import { useStore } from 'vuex'
import {useSwal} from "@/composables/useSwal";

const store = useStore()
const {showSwal} = useSwal()

const emits = defineEmits(['callback'])
const props = defineProps({
  id: { type: String, required: true },
  isDoor: { type: Boolean, default: false },
  currentConfig: { type: [], default: () => [] }
})

const formData = ref({
  ...props.currentConfig,
  accessMethods: props.currentConfig.accessMethods ? [...props.currentConfig.accessMethods] : []
})
const currentProcess = ref({})
const isLoading= ref(false)
const deleting= ref(false)
const canEditMethod= ref(false)
const updatingMethod = ref(false)

const accessMethods = computed(() => store.getters['methods/getMethods'])

watch(() => props.currentConfig, () => {
  formData.value = {
    ...props.currentConfig,
    accessMethods: props.currentConfig.accessMethods ? [...props.currentConfig.accessMethods] : []
  }
  setCurrentProcess(formData.value.accessMethods[0] ?? null)
})

function toggleEditMethod() {
  canEditMethod.value = !canEditMethod.value
}
function handleMethodAction() {
  canEditMethod.value
      ? setMethodConfig()
      : toggleEditMethod()
}

function setMethodConfig(){
  updatingMethod.value = true

  let data = {
    ids: currentProcess.value.methods.map(el => el.id),
    key: currentProcess.value.association_key,
    ...props.currentConfig
  }

  let endPoint = currentProcess.value.isEdit ? 'access/updateAccessMethods' : 'access/setAccessMethods'

  store.dispatch(endPoint, data)
      .then((response) => {
        toggleEditMethod()
        emits('callback')
        window.toastr['success'](i18n.global.t('notification.config_updated'))
      })
      .catch()
      .finally(() => updatingMethod.value = false)
}
function addNewProcess() {
  if (!formData.value?.accessMethods) {
    formData.value.accessMethods = []
  }
  formData.value.accessMethods.push({
    association_key: formData.value.accessMethods.length + 1,
    isEdit: false,
    methods: []
  })
  currentProcess.value = formData.value.accessMethods[formData.value.accessMethods.length - 1]
}
function removeProcess() {

  showSwal(
      '',
      i18n.global.t('access.swal.access_process_deleted'),
  ).then( async (result) => {
    if (result.value) {
      if(!currentProcess.value?.isEdit) {
        const index = formData.value.accessMethods.findIndex(el => el.association_key === currentProcess.value.association_key)
        if (index > -1) {
          formData.value.accessMethods.splice(index, 1)
        }
        currentProcess.value = null
        return
      }

      deleting.value = true

      let data = {
        key: currentProcess.value.association_key,
        ...props.currentConfig
      }

      store.dispatch('access/deleteAccessMethods', data)
          .then(() => {
            emits('callback')
          }).finally(() => deleting.value = false)
    }
  })


}
function addMethod(method) {
  if (!currentProcess.value?.methods) {
    currentProcess.value.methods = []
  }
  currentProcess.value.methods.push(method)
}
function removeMethod(index) {
  if (currentProcess.value?.methods && index > -1) {
    currentProcess.value?.methods.splice(index, 1)
  }
}
function setCurrentProcess(process) {
  if(process) {
    currentProcess.value = {...process, isEdit: process.methods && process.methods.length > 0}

  }else {
    currentProcess.value = null
  }
  canEditMethod.value = false
}

onMounted(() => {
  store.dispatch('methods/loadMethods')
})


</script>

<template>
  <div class="row px-3 my-4 ">
    <Card class="card shadow-sm border col-3" >
      <template #cardHeader>
        <div class="card-header p-1 d-flex justify-content-between align-items-center">
          <h4>Processes</h4>
          <button class="btn btn-sm btn-icon btn-primary" @click.prevent="addNewProcess"> <i class="fa fa-plus"></i> </button>
        </div>
      </template>
      <div class="card-body p-2">
        <Card
            class="card shadow-sm border bg-hover-light-info p-0 mb-2 mw-100"
            :class="{'bg-light-primary bg-hover-light-primary text-primary border border-primary fw-bold' : process.association_key === currentProcess.association_key}"
            v-for="(process, index) in formData.accessMethods"
            :key="'process_'+index"
            @click.prevent="setCurrentProcess(process)">
          <div class="card-body p-0 p-3">
            <div class="d-flex justify-content-between align-items-center">
              Process {{ index + 1 }}
            </div>
          </div>
        </Card>
      </div>
    </Card>
    <div class="col-9 ps-5">
      <Card class="card shadow-sm border">
        <Card v-if="formData.accessMethods && formData.accessMethods.length"  class="card">
          <template #cardBody>
            <div v-if="currentProcess && currentProcess?.methods?.length" class="card-body p-0 p-2">
              <div class="row">
                <div v-for="(method, index) in currentProcess.methods" :key="index" class="col-md-6 p-3">
                  <AccessMethodCard
                      :method="method"
                      :index="index"
                      :showRemoveBtn="canEditMethod && currentProcess.methods.length > 1"
                      :on-press-remove="removeMethod"
                  />
                </div>
              </div>
            </div>
            <div v-else class="card-body">
              <div class="d-flex justify-content-center">
              <span class="fs-2x text-center">
                 {{$t('access.configAccess.noMethodConfig') }}
              </span>
              </div>
            </div>
          </template>
          <template v-if="canEditMethod" #cardFooter>
            <div class="row">
              <div v-for="(method, index) in accessMethods" :key="id +'_'+index" class="col-md-6 p-3">
                <AccessMethodCard
                    :index="index"
                    :method="method"
                    :on-press="addMethod"
                    card-classes="cursor-pointer"
                />
              </div>
            </div>
          </template>
        </Card>
        <Card v-else>
          <template #cardBody>
            <div class="card-body">
              <div class="d-flex justify-content-center">
              <span class="fs-2x text-center">
                 {{$t('access.configAccess.noProcess') }}
              </span>
              </div>
            </div>
          </template>
        </Card>
      </Card>
      <div v-if="currentProcess" class="d-flex justify-content-end mt-5">
        <Button
            type="button"
            btn-class="btn btn-sm btn-primary"
            size="7px 30px"
            :btn-text="canEditMethod ? 'button.save' : 'button.edit'"
            :is-loading="updatingMethod"
            @click.prevent="handleMethodAction"
        />
        <Button
            type="button"
            btn-class="btn btn-sm btn-danger ms-2"
            size="7px 30px"
            btn-text="button.delete"
            :is-loading="deleting"
            @click.prevent="removeProcess"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>